/* eslint-disable no-unused-vars */
import { useState, useRef, useId } from 'react';
import { useSelector } from 'react-redux';
import { CustomButton, Label, CustomTooltip, Loader, Each, Separators } from 'components';
import * as S from './CreativeVideoUploader.styled';
import { Question } from 'assets/icons';
import { useComputed, useSignal } from '@preact/signals-react';
import { manageCreativeMediaAPI } from 'services/manageCreativeMedia.service';
import { ListItem } from '../generalModalUi/ListItem';

const toolTipStyle = {
  color: '#64748B',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: '16px',
};
const labelStyle = { marginLeft: 'auto', marginRight: '2px' };

const allowedExtensions = ['.mp4', '.mov'];

const checkVideoFile = (video, errors$, creativeModalType) => {
  const { videoWidth, videoHeight, duration } = video;
  const aspectRatio = videoWidth / videoHeight;
  if (creativeModalType === 'video_portrait' && aspectRatio >= 1.5) {
    errors$.value = 'The video does not suite for portrait';
    URL.revokeObjectURL(video.src);
    return false;
  }

  if (creativeModalType === 'video_landscape' && aspectRatio < 1.5) {
    errors$.value = 'The video does not suite for landscape';
    URL.revokeObjectURL(video.src);
    return false;
  }
  if (duration < 10 || duration > 60) {
    errors$.value = 'The video must be longer than 10 seconds and less than 60 seconds';
    URL.revokeObjectURL(video.src);
    return false;
  }
  errors$.value = '';
  return true;
};
const checkExtensions = (file, errors$) => {
  if (!file) return (errors$.value = 'File not selected');
  if (file) {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const allowedExt = allowedExtensions.map((ext) => ext.replace('.', ''));
    if (!allowedExt.includes(fileExtension)) {
      errors$.value = `The file extension must be one of the following: ${allowedExt.join(', ')}`;
      return false;
    }
    return true;
  }
};

const videoTypesList = {
  video_landscape: 'landscape',
  video_portrait: 'portrait',
};

export const CreativeVideoUploader = ({
  creativeModalType,
  selectedImgOrVideo$,
  filteredItems$,
  searchValue$,
  isImg,
  isVideo,
}) => {
  const errors$ = useSignal('');
  const btnId = useId();
  const [uploadCreativeVideoAction, uploadResult] =
    manageCreativeMediaAPI.useUploadCreativeVideoMutation();
  const inputRef = useRef();
  const pba_id = useSelector((state) => state.creatives.creativeAndTrackingData.pba_id);
  const app_package = useSelector((state) => state.creatives.creativeAndTrackingData.app_package);

  const [isInside, setIsInside] = useState(false);

  const uploadVideo = (file) => {
    if (!file) return (errors$.value = 'File not selected');
    const video = document.createElement('video');
    video.src = URL.createObjectURL(file);
    video.onloadedmetadata = () => {
      const res = checkVideoFile(video, errors$, creativeModalType);
      if (res) {
        const formData = new FormData();
        formData.append('videoName', file.name);
        //landscape || portrait
        formData.append('videoType', videoTypesList[creativeModalType]);
        formData.append('pba_id', pba_id);
        formData.append('package_id', app_package);
        formData.append('file', file);
        uploadCreativeVideoAction(formData);
      }
    };
  };

  const simulateFileInputClick = () => {
    inputRef.current.click();
    errors$.value = '';
    selectedImgOrVideo$.value = false;
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    errors$.value = '';
    const files = event.dataTransfer.files;

    setIsInside(false);
    if (files.length > 1) {
      errors$.value = 'Select only one file';
      return;
    }
    const resOfDragging = checkExtensions(files[0], errors$);
    if (resOfDragging) {
      uploadVideo(files[0]);
    }
  };
  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    setIsInside(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsInside(false);
    }
  };

  const showError$ = useComputed(() => {
    return !selectedImgOrVideo$.value && errors$.value;
  });

  return (
    <div
      onDrop={handleFileDrop}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
    >
      <S.Box>
        {uploadResult.isLoading && <Loader.Global delay={0} />}
        <S.DraggingContainer
        // onDrop={handleFileDrop}
        // onDragEnter={handleDragEnter}
        // onDragLeave={handleDragLeave}
        // onDragOver={handleDragOver}
        >
          <CustomButton
            btnWrapperStyle={{
              height: '32px',
              //color: isInside ? 'red' : 'inherit',
            }}
            square
            id={btnId}
            buttonColor={CustomButton.Colors.Secondary}
            onClick={simulateFileInputClick}
          >
            Upload Creative
          </CustomButton>
          <input
            ref={inputRef}
            type='file'
            style={{ display: 'none' }}
            onChange={(event) => uploadVideo(event.target.files[0])}
            accept={allowedExtensions.join(',')}
          />
          <Label
            variant='description'
            text='or Drag and drop files here'
            style={{
              whiteSpace: 'nowrap',
              //color: isInside ? 'red' : 'inherit',
            }}
          />
        </S.DraggingContainer>
        {useComputed(() => (
          <>
            {showError$.value && (
              <Label
                variant='description'
                style={{ color: 'red', flexGrow: '1' }}
                text={errors$.value}
              />
            )}
          </>
        ))}
        <Label text='Requirements' variant='description' style={labelStyle} />
        <CustomTooltip
          style={toolTipStyle}
          content={<Question />}
          text={
            <p style={{ maxWidth: '150px' }}>
              You can upload a video no less than 10 seconds and no more than 60 seconds.Please
              upload MP4 video file
            </p>
          }
        />
      </S.Box>
      <Separators type={Separators.separatorTypes.small} />
      {useComputed(() => {
        if (filteredItems$.value?.length) {
          return (
            <S.ItemsContainer>
              <Each
                fieldOfIndex='media_id'
                of={filteredItems$.value}
                render={(item, index) => {
                  return (
                    <ListItem
                      creativeModalType={creativeModalType}
                      selectedImgOrVideo$={selectedImgOrVideo$}
                      item={item}
                      searchValue$={searchValue$}
                      isImg={isImg}
                      isVideo={isVideo}
                    />
                  );
                }}
              />
            </S.ItemsContainer>
          );
        }
      })}
    </div>
  );
};
